// The gift engine (DNA2 B493, rebuilt 2026-10-27 on his word: "every object we sell can be treated as a potential gift").
// sections/ap-gift-finder.liquid renders the page a machine reads with no script (the hand-picked core set, the budget
// lists, an ItemList); this module is the enhancement: it RANKS the whole catalogue, it never filters it to nothing.
//
//  · the catalogue: the same section rendered through Shopify's Section Rendering API on the all-products collection
//    (/collections/all?section_id=ap-gift-finder&sort_by=best-selling&page=N, 250 a page, every page fetched), so the
//    order is the store's own best-selling order and the engine keeps up as the catalogue grows with no theme edit;
//    the storefront's /collections/all/products.json is the fallback. Cached in sessionStorage for 30 minutes.
//  · every product on the Online Store is a candidate, except a gift card, a product tagged `gift:no`, and a product with
//    no variant for sale;
//  · each choice ADDS to a score (who it is for, the moment, what they would use); the budget is the only hard limit and
//    it relaxes in plain sight: when fewer than `min` gifts inside it answer the choices ("nothing at that price for
//    X"), the gifts that do answer them follow from just outside it, closest price first, under a line that says so;
//  · results never fall below `min` while the catalogue holds that many gifts; each says why it is there.
// No HTML strings are written (every node is built), and every address is checked against this origin.

const OBJECTS = 'Ugly Christmas Sweatshirt|Ugly Christmas Sweater|Christmas Sweater|Holiday Sweatshirt|Holiday Sweater|Sweatshirt|Sweater|Hoodie|Rocks glass 10.5 oz|Rocks Glass|Whiskey Glass|Can-shaped glass 16 oz|Can Glass|Pint Glass|Lawyer Coffee Mug|Coffee Mug|Mug 11 oz|Mug|Foam Trucker Hat|Trucker Hat|Dad Cap|Dad Hat|Lawyer Hat|Cap pair|Cap|Hat|Bumper sticker 15×3.75|Car magnet 10×3|Car Magnet|Laptop Sticker|Sticker|Water Bottle|Skinny Tumbler|Wine Tumbler|Tumbler|Laptop Sleeve|Coupon Pad|Notepad|Journal|Nameplate Bar Plaque|Acrylic Plaque|Plaque|Lawyer Desk Mat|Desk Mat|Lawyer Mouse Pad|Mouse Pad|Baby bodysuit|Baby Bodysuit|Bodysuit|Candle|Beanie|Lawyer Christmas Ornament|Christmas Ornament|Ornament|Drink Coaster|Coaster|Tote Bag|Tote|Tee, back print|Tee pair|T-Shirt|Tee|pair Socks|Socks|Doormat|Necktie|Tie'.split('|');

const removeLast = (s, sub) => {
  const i = sub ? s.lastIndexOf(sub) : -1;
  return i < 0 ? s : s.slice(0, i) + s.slice(i + sub.length);
};
const removeFirst = (s, sub) => {
  const i = sub ? s.indexOf(sub) : -1;
  return i < 0 ? s : s.slice(0, i) + s.slice(i + sub.length);
};
const lsplit = (s, sep) => {
  const parts = s.split(sep);
  while (parts.length && parts[parts.length - 1] === '') parts.pop(); // Liquid's split drops trailing empties
  return parts;
};
const endsInObject = text => {
  for (const o of OBJECTS) {
    const suffix = ' ' + o;
    const pre = removeLast(text, suffix);
    if (pre + suffix === text && pre.trim() !== '') return true;
  }
  return false;
};

/** A card's title as the joke and the object: snippets/docket-card-split, the same steps in the same order. */
export function splitTitle(title) {
  let joke = title;
  let object = '';
  let head = title;
  let tailed = false;
  const parts = lsplit(title, ': ');
  if (parts.length > 1) {
    const cand = removeLast(removeLast(title, parts[parts.length - 1]), ': ');
    if (endsInObject(cand)) {
      head = cand;
      tailed = true;
    }
  }
  if (!tailed) {
    const fparts = lsplit(title, ' for ');
    if (fparts.length > 1) {
      let acc = '';
      for (const fp of fparts) {
        if (acc !== '') acc += ' for ';
        acc += fp;
        if (acc === title) break;
        if (endsInObject(acc)) {
          head = acc;
          tailed = true;
          break;
        }
      }
    }
  }
  const court = removeLast(removeFirst(head, 'Barred Cap ('), ')');
  if ('Barred Cap (' + court + ')' === head && court.includes(' Circuit') && court !== head) {
    joke = ('BARRED · ' + court).toUpperCase();
    object = 'Cap';
  }
  let core = head;
  let qual = '';
  if (object === '' && head.slice(-1) === ')' && head.includes(' (') && !head.includes('Barred Cap (')) {
    qual = ' (' + head.split(' (').pop();
    core = removeLast(head, qual);
  }
  for (const o of OBJECTS) {
    if (object !== '') break;
    const tail = ' ' + o;
    const front = removeLast(core, tail);
    if (front + tail === core && front.trim() !== '') {
      joke = front;
      object = o + qual;
    }
  }
  return { joke, object };
}

/** What they would use: read from the product type (and the object), so a new type lands in a use with no edit. */
export const USES = {
  wear: { p: /\b(hat|cap|beanie|t-shirt|shirt|tee|hoodie|sweatshirt|sweater|knit|socks?|necktie|tie|bodysuit|jacket)\b/i, s: null, say: 'to wear', sayS: '' },
  drink: { p: /\b(mug|tumbler|bottle|glass|glassware|cup|flask)\b/i, s: /\bcoaster\b/i, say: 'to drink from', sayS: 'for drinks' },
  desk: { p: /\b(plaque|mouse ?pad|desk|journal|notepad|notebook|laptop|sleeve)\b/i, s: /\b(mug|coaster|sticker|tumbler)\b/i, say: 'for the desk', sayS: 'at a desk too' },
  home: { p: /\b(doormat|ornament|candle|blanket|pillow|poster|coaster)\b/i, s: /\b(plaque|glassware|glass|mug|tote)\b/i, say: 'for the house', sayS: 'at home too' },
  car: { p: /\b(car magnet|bumper)\b/i, s: /\b(tumbler|bottle|sticker|magnet)\b/i, say: 'for the car', sayS: 'rides along in a car' },
  small: { p: /\b(sticker|ornament|coaster|magnet|notepad|socks?)\b/i, s: /\b(mouse ?pad|necktie)\b/i, cheap: 1500, say: 'small and easy to give', sayS: 'a small price' },
};

export const WEIGHTS = { rTag: 10, rWord: 6, rAny: 4, mTag: 10, mRel: 6, mWord: 5, uP: 8, uS: 3, pop: 2, hero: 2, design: 7, type: 2 };

const words = list => {
  const ws = String(list || '')
    .split(',')
    .map(w => w.trim().toLowerCase())
    .filter(Boolean)
    .map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return ws.length ? new RegExp('(^|[^a-z0-9])(' + ws.join('|') + ')($|[^a-z0-9])', 'i') : null;
};

const designKey = joke =>
  joke
    .toLowerCase()
    .replace(/^barred in .*/, 'barred in')
    .replace(/.*\bcircuit\b.*/, 'circuit')
    .replace(/\bamendments?\b.*/, 'amendment')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** The engine's configuration, made once: the section's JSON with its words compiled. */
export function compile(cfg) {
  const out = { min: 8, show: 12, ...cfg };
  out.recipients = (cfg.recipients || []).map(r => ({ ...r, tag: (r.tag || 'for:' + r.key).toLowerCase(), re: words(r.words) }));
  out.moments = (cfg.moments || []).map(m => ({
    ...m,
    tag: (m.tag || 'occasion:' + m.key).toLowerCase(),
    re: words(m.words),
    rel: String(m.related || '')
      .split(',')
      .map(t => t.trim().toLowerCase())
      .filter(Boolean),
  }));
  out.bands = (cfg.bands || []).map(b => ({ ...b, lo: Math.round((Number(b.min) || 0) * 100), hi: b.max === null || b.max === '' || b.max === undefined ? Infinity : Math.round(Number(b.max) * 100) }));
  out.core = cfg.core || [];
  return out;
}

/** The catalogue's raw rows (the section's data mode, or products.json mapped to the same shape) made rankable. */
export function prepare(rows, cfg) {
  const seen = new Set();
  const items = [];
  rows.forEach(r => {
    if (!r || !r.h || seen.has(r.h)) return;
    seen.add(r.h);
    const tags = new Set((r.g || []).map(t => String(t).trim().toLowerCase()));
    const prices = [...new Set((r.p || []).map(Number).filter(n => n > 0))].sort((a, b) => a - b);
    if (!prices.length || tags.has('gift:no') || /gift ?card/i.test(r.y || '')) return; // not for sale, opted out, or a gift card
    const { joke, object } = splitTitle(String(r.n || r.h));
    items.push({
      h: r.h,
      n: String(r.n || ''),
      j: joke,
      o: object,
      y: String(r.y || ''),
      g: tags,
      p: prices,
      i: r.i || '',
      d: designKey(object ? joke : String(r.n || r.h).split(': ')[0]),
      hay: (String(r.n || '') + ' ' + String(r.s || '')).toLowerCase(), // the title and its SEO title (the gift phrase, answer 5)
      kind: (String(r.y || '') + ' ' + object).trim(),
      hero: cfg.core.indexOf(r.h),
      rank: items.length,
    });
  });
  return items;
}

const inBand = (it, b) => it.p.some(c => c >= b.lo && c <= b.hi);
const bandPrice = (it, b) => (b && it.p.find(c => c >= b.lo && c <= b.hi)) || it.p[0];
const nearPrice = (it, b) => it.p.reduce((best, c) => (Math.abs(c - (c < b.lo ? b.lo : b.hi)) < Math.abs(best - (best < b.lo ? b.lo : b.hi)) ? c : best), it.p[0]);
const distance = (it, b) => Math.min(...it.p.map(c => (c < b.lo ? b.lo - c : c > b.hi ? c - b.hi : 0)));
const has = (it, tag) => it.g.has(tag) || (tag.startsWith('type:') && it.y.toLowerCase() === tag.slice(5));

/** Why one product answers the choices: the reasons it scored, one per dimension. */
function reasons(it, ch, cfg) {
  const why = {};
  if (ch.r) {
    if (it.g.has(ch.r.tag)) why.r = 'tag';
    else if (ch.r.re && ch.r.re.test(it.hay)) why.r = 'word';
    else if (ch.r.anyLawyer && it.g.has('for:every-lawyer')) why.r = 'any';
  }
  if (ch.m) {
    if (it.g.has(ch.m.tag)) why.m = 'tag';
    else if (ch.m.rel.some(t => has(it, t))) why.m = 'rel';
    else if (ch.m.re && ch.m.re.test(it.hay)) why.m = 'word';
  }
  if (ch.u) {
    if (ch.u.p.test(it.kind)) why.u = 'p';
    else if ((ch.u.s && ch.u.s.test(it.kind)) || (ch.u.cheap && it.p[0] <= ch.u.cheap)) why.u = 's';
  }
  return why;
}

function score(it, why, total, cfg) {
  const W = WEIGHTS;
  let s = W.pop * (1 - it.rank / Math.max(total, 1));
  if (it.hero >= 0) s += W.hero + 1 - it.hero / Math.max(cfg.core.length, 1);
  if (why.r) s += why.r === 'tag' ? W.rTag : why.r === 'word' ? W.rWord : W.rAny;
  if (why.m) s += why.m === 'tag' ? W.mTag : why.m === 'rel' ? W.mRel : W.mWord;
  if (why.u) s += why.u === 'p' ? W.uP : W.uS;
  return s;
}

/** Best first, but one design does not fill the page: a repeat of a design (or a third of a type) waits its turn. */
function diversify(sorted, k) {
  const picked = [];
  const rest = sorted.slice();
  const designs = new Map();
  const types = new Map();
  while (picked.length < k && rest.length) {
    let best = 0;
    let bestScore = -Infinity;
    for (let i = 0; i < Math.min(rest.length, 120); i++) {
      const e = rest[i];
      const adj = e.s - WEIGHTS.design * (designs.get(e.it.d) || 0) - WEIGHTS.type * Math.max(0, (types.get(e.it.y) || 0) - 1);
      if (adj > bestScore) {
        bestScore = adj;
        best = i;
      }
    }
    const [e] = rest.splice(best, 1);
    designs.set(e.it.d, (designs.get(e.it.d) || 0) + 1);
    types.set(e.it.y, (types.get(e.it.y) || 0) + 1);
    picked.push(e);
  }
  return picked.concat(rest);
}

/** The choices by key ('' or absent is "any") resolved against the configuration. */
export function resolve(choices, cfg) {
  return {
    r: cfg.recipients.find(r => r.key === choices.for) || null,
    m: cfg.moments.find(m => m.key === choices.moment) || null,
    u: USES[choices.use] || null,
    uk: USES[choices.use] ? choices.use : '',
    b: cfg.bands.find(b => b.key === choices.budget) || null,
  };
}

/**
 * Rank the catalogue for the choices. Every candidate gets a TIER (how many of the chosen who / moment / use it answers)
 * and a score; the list runs tier first, score second, one design not filling the page. It comes in three runs:
 *   fit    inside the budget and answering at least one choice (with no choice but a budget: everything inside it);
 *   near   only when fewer than `min` gifts fit: the gifts that answer the choices from outside the budget, the best
 *          answers first and the closest price next, enough to fill the first screen (all of them when nothing else
 *          is inside the budget); the page says so above them;
 *   other  the rest inside the budget, after a line that says they are outside the choices;
 *   and, only if all that is still short of `min`, the rest of the catalogue by closest price, marked near.
 * Returns { list, fit, near, ch, seeAll }: `list` entries carry .seg ('fit' | 'near' | 'other'), .why and .s.
 */
export function rank(items, choices, cfg) {
  const ch = resolve(choices, cfg);
  const total = items.length;
  const asked = ['r', 'm', 'u'].filter(k => ch[k]);
  const scored = items.map(it => {
    const why = reasons(it, ch, cfg);
    // a specific answer counts 1, a generic one (a gift for every lawyer, a secondary use) a half
    const tier = asked.reduce((t, k) => t + (!why[k] ? 0 : why[k] === 'any' || why[k] === 's' ? 0.5 : 1), 0);
    return { it, why, s: score(it, why, total, cfg), tier };
  });
  const byScore = (a, b) => b.tier - a.tier || b.s - a.s || a.it.rank - b.it.rank;
  const tiered = list => {
    const out = [];
    [...new Set(list.map(e => e.tier))].sort((a, b) => b - a).forEach(t => out.push(...diversify(list.filter(e => e.tier === t).sort(byScore), cfg.show * 3)));
    return out;
  };
  const mark = (list, seg) => list.map(e => ({ ...e, seg }));
  if (!asked.length && !ch.b) {
    // the first screen: his hand-picked set in his order, topped up from the best sellers
    const heroes = scored.filter(e => e.it.hero >= 0).sort((a, b) => a.it.hero - b.it.hero);
    const list = mark(heroes.concat(diversify(scored.filter(e => e.it.hero < 0).sort(byScore), cfg.show * 3)), 'fit');
    return { list, fit: list.length, near: 0, ch, seeAll: seeAll(items, ch, cfg) };
  }
  const inside = ch.b ? scored.filter(e => inBand(e.it, ch.b)) : scored;
  const fits = tiered(inside.filter(e => !asked.length || e.tier > 0));
  const others = tiered(inside.filter(e => asked.length && e.tier === 0));
  let near = [];
  if (ch.b && fits.length < cfg.min) {
    // the best answers first; among equal answers the closest price (a dollar away costs a point), one design at a time
    near = tiered(
      scored
        .filter(e => !inBand(e.it, ch.b) && (!asked.length || e.tier > 0))
        .map(e => {
          const dist = distance(e.it, ch.b);
          return { ...e, dist, s: e.s - dist / 100 };
        })
    );
    if (others.length) near = near.slice(0, Math.max(cfg.min - fits.length, cfg.show - fits.length - others.length));
  }
  let list = mark(fits, 'fit').concat(mark(near, 'near'), mark(others, 'other'));
  if (ch.b && asked.length && list.length < cfg.min) {
    // still short of the floor: the rest of the catalogue, closest price first
    const rest = scored
      .filter(e => !inBand(e.it, ch.b) && e.tier === 0)
      .map(e => ({ ...e, dist: distance(e.it, ch.b) }))
      .sort((a, b) => a.dist - b.dist || b.s - a.s || a.it.rank - b.it.rank);
    list = list.concat(mark(rest, 'near'));
  }
  const strong = fits.filter(e => asked.every(k => e.why[k])).length;
  return { list, fit: fits.length, strong, near: list.filter(e => e.seg === 'near').length, ch, seeAll: seeAll(items, ch, cfg) };
}

/** The "see all" address: the most specific real collection page that is not empty, with Shopify's own filters. */
function seeAll(items, ch, cfg) {
  const all = { url: cfg.allUrl || '/collections/all', test: () => true, ctx: '' };
  const base = ch.r ? { url: ch.r.url, test: it => it.g.has(ch.r.tag), ctx: 'made for ' + ch.r.who, name: ch.r.label } : ch.m && ch.m.url ? { url: ch.m.url, test: it => it.g.has(ch.m.tag), ctx: ch.m.phrase, name: ch.m.label } : null;
  const types = ch.u ? [...new Set(items.filter(it => ch.u.p.test(it.kind)).map(it => it.y).filter(Boolean))].sort() : [];
  const typeF = types.length ? { test: it => types.includes(it.y), ctx: ch.u.say } : null;
  const bandF = ch.b ? { test: it => inBand(it, ch.b), ctx: bandPhrase(ch.b, cfg) } : null;
  const tries = [];
  for (const b of [base, all].filter(Boolean)) {
    tries.push([b, typeF, bandF], [b, null, bandF], [b, typeF, null], [b, null, null]);
  }
  for (const [b, t, p] of tries) {
    const n = items.filter(it => b.test(it) && (!t || t.test(it)) && (!p || p.test(it))).length;
    if (!n) continue;
    const params = [];
    if (t) types.forEach(y => params.push(['filter.p.product_type', y]));
    if (p) {
      if (ch.b.lo > 0) params.push(['filter.v.price.gte', String(ch.b.lo / 100)]);
      if (ch.b.hi !== Infinity) params.push(['filter.v.price.lte', String(ch.b.hi / 100)]);
    }
    params.push(['sort_by', 'best-selling']);
    const qs = params.map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(v)).join('&');
    return { url: b.url + '?' + qs, n, ctx: [b.ctx, t && t.ctx, p && p.ctx].filter(Boolean).join(', '), name: b.name || '', filters: [t && t.ctx, p && p.ctx].filter(Boolean).join(', ') };
  }
  return { url: all.url + '?sort_by=best-selling', n: items.length, ctx: '', name: '', filters: '' };
}

/**
 * The "see all" link's words name what it opens (his shop notes of 2026-10-29, store-upkeep action item 36): "See all N
 * gifts" only for the whole catalogue; a recipient's or a moment's own collection by its name ("Open Trial lawyer: 37 gifts,
 * under $25"); the whole catalogue narrowed by Shopify's filters as "See the N gifts under $25".
 */
export function seeAllLabel(target) {
  const gifts = n => n + (n === 1 ? ' gift' : ' gifts');
  if (target.name) return 'Open ' + target.name + ': ' + gifts(target.n) + (target.filters ? ', ' + target.filters : '') + ' →';
  if (target.ctx) return 'See the ' + gifts(target.n) + ' ' + target.ctx + ' →';
  return 'See all ' + gifts(target.n) + ' →';
}

const dollars = (cents, cfg) => money(cents, cfg).replace(/\.00$/, '');
export function bandPhrase(b, cfg) {
  if (b.lo <= 0) return 'under ' + dollars(b.hi, cfg);
  if (b.hi === Infinity) return 'over ' + dollars(b.lo, cfg);
  return 'between ' + dollars(b.lo, cfg) + ' and ' + dollars(b.hi, cfg);
}

export function money(cents, cfg) {
  try {
    return new Intl.NumberFormat(cfg.locale || 'en-US', { style: 'currency', currency: cfg.currency || 'USD' }).format(cents / 100);
  } catch {
    return '$' + (cents / 100).toFixed(2);
  }
}

const cap = s => (s ? s[0].toUpperCase() + s.slice(1) : s);

/** The plain "why this gift" line: what it answers among the choices, else what it is made for. */
export function whyLine(e, ch, cfg) {
  const it = e.it;
  const parts = [];
  const matched = e.tier > 0;
  if (e.seg === 'near') {
    const c = nearPrice(it, ch.b);
    parts.push(c < ch.b.lo ? 'below your range' : 'over your budget');
  }
  if (ch.r && matched) {
    if (e.why.r === 'tag') parts.push('made for ' + ch.r.who);
    else if (e.why.r === 'word') parts.push('a joke ' + ch.r.who + ' will get');
    else if (e.why.r === 'any') parts.push('for any lawyer, ' + ch.r.who + ' included');
  } else {
    const own = cfg.recipients.find(r => it.g.has(r.tag));
    if (own) parts.push('made for ' + own.who);
  }
  if (ch.m && matched) {
    if (e.why.m) parts.push('a gift ' + ch.m.phrase);
  } else {
    const own = cfg.moments.find(m => it.g.has(m.tag));
    if (own && parts.length < 2) parts.push('a gift ' + own.phrase);
  }
  if (ch.u && e.why.u) parts.push(e.why.u === 'p' ? ch.u.say : ch.u.sayS);
  if (!parts.length) return 'A sound gift for anyone in law.';
  return cap(parts.slice(0, 3).join(' · ')) + '.';
}

/** The line above the near run and above the other run. */
export function segmentNote(seg, res, cfg, after) {
  const ch = res.ch;
  if (seg === 'near' && after === 'other') return 'Outside your budget, the closest by price:';
  const what = [ch.r && 'for ' + ch.r.who, ch.m && ch.m.phrase, ch.u && ch.u.say].filter(Boolean).join(', ');
  if (seg === 'near') return (res.fit ? 'Nothing else ' : 'Nothing ') + (what ? what + ' ' : '') + bandPhrase(ch.b, cfg) + (res.fit || what ? '' : ' yet') + (what ? '. The nearest, at other prices:' : '. The closest by price:');
  return ch.b ? 'Other gifts ' + bandPhrase(ch.b, cfg) + ':' : 'Other good gifts:';
}

// ------------------------------------------------------------------------------------------------ the page

const TTL = 30 * 60 * 1000;
const sameOrigin = href => {
  const u = new URL(href, location.origin);
  if (u.origin !== location.origin) throw new Error('apg: not this store');
  return u;
};

async function pages(first, fetchPage) {
  const out = [first];
  const left = [];
  for (let n = 2; n <= (first.pages || 1); n++) left.push(n);
  while (left.length) out.push(...(await Promise.all(left.splice(0, 4).map(fetchPage)))); // four at a time
  return out.flatMap(d => d.items || []);
}

async function viaSection(cfg) {
  const fetchPage = async n => {
    const u = sameOrigin(cfg.allUrl);
    u.searchParams.set('section_id', 'ap-gift-finder');
    u.searchParams.set('sort_by', 'best-selling');
    u.searchParams.set('page', String(n));
    const r = await fetch(u, { credentials: 'same-origin' });
    if (!r.ok) throw new Error('apg: ' + r.status);
    const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
    const node = doc.querySelector('script[data-apg-data]');
    if (!node) throw new Error('apg: no data');
    return JSON.parse(node.textContent);
  };
  return pages(await fetchPage(1), fetchPage);
}

async function viaProductsJson(cfg) {
  const fetchPage = async n => {
    const u = sameOrigin(cfg.allUrl.replace(/\/$/, '') + '/products.json');
    u.searchParams.set('limit', '250');
    u.searchParams.set('page', String(n));
    const r = await fetch(u, { credentials: 'same-origin' });
    if (!r.ok) throw new Error('apg: ' + r.status);
    const data = await r.json();
    const items = (data.products || []).map(p => ({
      h: p.handle,
      n: p.title,
      y: p.product_type,
      g: Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(','),
      p: (p.variants || []).filter(v => v.available).map(v => Math.round(parseFloat(v.price) * 100)),
      i: p.images && p.images[0] ? p.images[0].src + (p.images[0].src.includes('?') ? '&' : '?') + 'width=400' : '',
    }));
    return { items, more: items.length === 250 };
  };
  const out = [];
  for (let n = 1; n <= 40; n++) {
    const d = await fetchPage(n);
    out.push(...d.items);
    if (!d.more) break;
  }
  return out;
}

async function loadCatalogue(cfg) {
  const key = 'apg:v1:' + cfg.count + ':' + cfg.allUrl;
  try {
    const hit = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (hit && Date.now() - hit.at < TTL && Array.isArray(hit.rows)) return hit.rows;
  } catch {}
  let rows;
  try {
    rows = await viaSection(cfg);
  } catch {
    rows = await viaProductsJson(cfg);
  }
  try {
    Object.keys(sessionStorage)
      .filter(k => k.startsWith('apg:'))
      .forEach(k => sessionStorage.removeItem(k));
    sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), rows }));
  } catch {}
  return rows;
}

const el = (tag, cls, text) => {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
};

function card(e, ch, cfg) {
  const it = e.it;
  const li = el('li', 'apg-card' + (e.seg === 'near' ? ' apg-card--near' : ''));
  const a = el('a', 'apg-card__link');
  a.href = sameOrigin(String(cfg.root || '/').replace(/\/$/, '') + '/products/' + encodeURIComponent(it.h)).pathname;
  const media = el('span', 'apg-card__media');
  if (it.i) {
    const img = el('img');
    img.src = it.i;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = 400;
    img.height = 400;
    media.append(img);
  }
  const name = el('span', 'apg-card__name');
  name.append(el('span', 'docket-card__joke', it.j));
  if (it.o) name.append(el('span', 'docket-card__object', it.o));
  a.append(media, name);
  const inside = ch.b && e.seg !== 'near';
  const price = e.seg === 'near' ? nearPrice(it, ch.b) : bandPrice(it, inside ? ch.b : null);
  const varies = it.p.length > 1 && !ch.b;
  li.append(a, el('p', 'apg-card__price', (varies ? 'From ' : '') + money(price, cfg)), el('p', 'apg-card__why', whyLine(e, ch, cfg)));
  return li;
}

function describe(ch, cfg) {
  return [[ch.r && 'for ' + ch.r.who, ch.m && ch.m.phrase, ch.u && ch.u.say].filter(Boolean).join(', '), ch.b && bandPhrase(ch.b, cfg)].filter(Boolean).join(' ');
}

function boot(root) {
  let cfg;
  try {
    cfg = compile(JSON.parse(root.querySelector('script[data-apg-config]').textContent));
    sameOrigin(cfg.allUrl);
  } catch {
    return; // the page as rendered stands
  }
  const form = root.querySelector('[data-apg-form]');
  const listEl = root.querySelector('[data-apg-list]');
  const title = root.querySelector('[data-apg-title]');
  const status = root.querySelector('[data-apg-status]');
  const more = root.querySelector('[data-apg-more]');
  const allLink = root.querySelector('[data-apg-all]');
  const reset = root.querySelector('[data-apg-reset]');
  if (!form || !listEl || !status) return;
  const coreTitle = title ? title.textContent : '';
  let items = null;
  let shown = cfg.show;
  let failed = false;

  const read = () => {
    const fd = new FormData(form);
    return { for: fd.get('for') || '', moment: fd.get('moment') || '', use: fd.get('use') || '', budget: fd.get('budget') || '' };
  };
  const write = choices => {
    const u = new URL(location.href);
    ['for', 'moment', 'use', 'budget'].forEach(k => (choices[k] ? u.searchParams.set(k, choices[k]) : u.searchParams.delete(k)));
    history.replaceState(history.state, '', u);
  };

  // a moment the store tags but the section does not name still gets its chip, and a named one with no gift loses it
  const syncMoments = () => {
    const group = form.querySelector('[data-apg-moments]');
    if (!group) return;
    const tags = new Set();
    items.forEach(it => it.g.forEach(t => t.startsWith('occasion:') && t.length > 9 && tags.add(t)));
    cfg.moments.forEach(m => {
      const live = items.some(it => it.g.has(m.tag) || m.rel.some(t => has(it, t)));
      const chip = group.querySelector('input[value="' + CSS.escape(m.key) + '"]');
      if (chip && !live && !chip.checked) chip.closest('label').hidden = true;
      tags.delete(m.tag);
    });
    tags.forEach(tag => {
      const key = tag.slice(9);
      const label = cap(key.replace(/-/g, ' '));
      cfg.moments.push({ key, label, tag, phrase: 'for ' + label.toLowerCase(), re: null, rel: [], url: null });
      const chip = el('label', 'apg-chip');
      const input = el('input', 'apg-chip__input');
      input.type = 'radio';
      input.name = 'moment';
      input.value = key;
      chip.append(input, el('span', 'apg-chip__face', label));
      group.append(chip);
    });
  };

  const render = () => {
    const choices = read();
    write(choices);
    const anyChoice = Object.values(choices).some(Boolean);
    if (reset) reset.hidden = !anyChoice;
    if (!items) {
      if (anyChoice) status.textContent = failed ? 'The catalogue did not load. The lists below still work.' : 'Loading the catalogue…';
      return;
    }
    const res = rank(items, choices, cfg);
    const ch = res.ch;
    const nodes = [];
    const visible = res.list.slice(0, Math.max(shown, cfg.min));
    visible.forEach((e, i) => {
      if (e.seg !== 'fit' && (i === 0 || visible[i - 1].seg !== e.seg)) nodes.push(el('li', 'apg-grid__note', segmentNote(e.seg, res, cfg, i ? visible[i - 1].seg : '')));
      nodes.push(card(e, ch, cfg));
    });
    listEl.replaceChildren(...nodes);
    const desc = describe(ch, cfg);
    const asked = [ch.r, ch.m, ch.u].filter(Boolean).length;
    if (title) title.textContent = anyChoice ? 'Best matches' : coreTitle;
    if (!anyChoice) status.textContent = 'Picked by hand. Choose who it is for, the moment, what they would use or a budget, and all ' + items.length + ' are ranked for you.';
    else if (res.near) status.textContent = (res.fit ? 'Only ' + res.fit + (res.fit === 1 ? ' gift ' : ' gifts ') : 'No gifts ') + desc + '. Showing the closest by price.';
    else if (asked < 2) status.textContent = 'Showing ' + Math.min(visible.length, res.fit) + ' of ' + res.fit + ' gifts ' + desc + ', best match first.';
    else status.textContent = (res.strong ? res.strong + (res.strong === 1 ? ' gift answers' : ' gifts answer') + ' every choice; ' : 'No gift answers every choice; ') + res.fit + ' answer at least one' + (ch.b ? ', all ' + bandPhrase(ch.b, cfg) : '') + '. Best match first.';
    if (more) {
      const left = res.list.length - visible.length;
      more.hidden = left <= 0;
      more.textContent = 'Show ' + Math.min(cfg.show, left) + ' more';
    }
    if (allLink) {
      const target = anyChoice ? res.seeAll : { url: cfg.allUrl + '?sort_by=best-selling', n: items.length, ctx: '' };
      allLink.href = sameOrigin(target.url).href;
      allLink.textContent = seeAllLabel(target);
    }
  };

  form.addEventListener('change', () => {
    shown = cfg.show;
    render();
  });
  form.addEventListener('submit', event => event.preventDefault());
  if (more)
    more.addEventListener('click', () => {
      shown += cfg.show;
      render();
    });
  if (reset)
    reset.addEventListener('click', () => {
      form.querySelectorAll('input[type="radio"][value=""]').forEach(input => (input.checked = true));
      shown = cfg.show;
      render();
    });

  // choices carried in the address (a shared link, the back button)
  const params = new URL(location.href).searchParams;
  ['for', 'moment', 'use', 'budget'].forEach(k => {
    const v = params.get(k);
    const input = v && form.querySelector('input[name="' + k + '"][value="' + CSS.escape(v) + '"]');
    if (input) input.checked = true;
  });
  root.classList.add('apg--live');
  render();

  loadCatalogue(cfg)
    .then(rows => {
      items = prepare(rows, cfg);
      syncMoments();
      const restored = form.querySelector('input[name="moment"][value="' + CSS.escape(params.get('moment') || '') + '"]');
      if (restored) restored.checked = true;
      const rendered = listEl.querySelectorAll('.apg-card').length;
      if (Object.values(read()).some(Boolean) || rendered < cfg.min) render();
      else status.textContent = 'Picked by hand. Choose who it is for, the moment, what they would use or a budget, and all ' + items.length + ' are ranked for you.';
    })
    .catch(() => {
      failed = true;
      render();
    });
}

if (typeof document !== 'undefined') document.querySelectorAll('[data-apg]').forEach(boot);
